import { useState, useMemo, useEffect } from "react";
import AppLayout from "../../components/AppLayout";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Grid,
  Stack,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import axios from "axios";
import {jwtDecode} from "jwt-decode";
import { toast } from "react-toastify";
import API_BASE_URL from "../../config/api";

const API_URL = `${API_BASE_URL}/api`;

export default function MyTasks() {
  const [searchQuery, setSearchQuery] = useState("");
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [taskToUndo, setTaskToUndo] = useState(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [productViewOpen, setProductViewOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [productViewLoading, setProductViewLoading] = useState(false);

  useEffect(() => {
    fetchMyTasks();
  }, []);

  const fetchMyTasks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const decoded = jwtDecode(token);
      
      // Get staff details from staff endpoint
      const staffResponse = await axios.get(`${API_URL}/staff/user/${decoded.userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const staffId = staffResponse.data.StaffId;
      
      if (!staffId) {
        setTasksData([]);
        toast.error("Staff ID not found. Please contact administrator.");
        return;
      }

      // Get tasks for this staff member
      const response = await axios.get(`${API_URL}/tasks/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        
        // Transform tasks to match the expected format
        const transformedTasks = response.data.tasks.map((task) => ({
          id: task.TaskId,
          taskId: task.TaskId,
          name: `${task.TaskType}: ${task.ProductName}`,
          description: task.Description,
          date: new Date(task.DueDate).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          dueDate: task.DueDate,
          assignee: staffResponse.data.FullName,
          priority: task.Priority.toLowerCase(),
          status: task.Status,
          assignmentStatus: task.AssignmentStatus, // Staff's completion status
          assignmentCompletedAt: task.AssignmentCompletedAt,
          customerName: task.CustomerName,
          customerPhone: task.CustomerPhone,
          billNumber: task.BillNumber,
          productCode: task.ProductCode, // Product code
          productName: task.ProductName, // Product name
          productSize: task.ProductSize,
          cost: task.CustomizationCost,
          productCategory: task.ProductCategory,
          paymentAmount: task.PaymentAmount || 0, // Payment amount for this task
        }));
        
        setTasksData(transformedTasks);
      } else {
        setTasksData([]);
      }
    } catch (error) {
      console.error("❌ Error fetching tasks:", error);
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to load tasks");
      setTasksData([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    let filtered = tasksData;
    
    // Apply search filter if query exists
    if (searchQuery) {
      filtered = tasksData.filter(
        (task) =>
          task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.billNumber.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.productCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.assignee.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort: Incomplete assignments first, then completed assignments
    const sorted = [...filtered].sort((a, b) => {
      // If one assignment is completed and the other isn't, completed goes last
      if (a.assignmentStatus === "Completed" && b.assignmentStatus !== "Completed") return 1;
      if (a.assignmentStatus !== "Completed" && b.assignmentStatus === "Completed") return -1;
      
      // Otherwise, sort by due date (earliest first)
      return new Date(a.dueDate) - new Date(b.dueDate);
    });
    
    return sorted;
  }, [searchQuery, tasksData]);

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedTask(null);
  };

  const fetchProductForView = async (productCode) => {
    if (!productCode) return;
    setProductViewLoading(true);
    setProductViewOpen(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/inventory/products/${encodeURIComponent(productCode)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setViewProduct(data.product);
      } else {
        toast.error("Could not load product details");
        setProductViewOpen(false);
      }
    } catch {
      toast.error("Failed to fetch product");
      setProductViewOpen(false);
    } finally {
      setProductViewLoading(false);
    }
  };

  const handleOpenCompletionDialog = (task) => {
    setSelectedTask(task);
    setCompletionDialogOpen(true);
  };

  const handleCloseCompletionDialog = () => {
    setCompletionDialogOpen(false);
    setSelectedTask(null);
    setCompletionNotes("");
  };

  const handleCompleteTask = async () => {
    if (!selectedTask) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/tasks/${selectedTask.taskId}/assignment/complete`,
        { completionNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Task marked as complete!");
        handleCloseCompletionDialog();
        fetchMyTasks(); // Refresh the task list
      }
    } catch (error) {
      console.error("Error completing task:", error);
      toast.error(error.response?.data?.error || "Failed to complete task");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenUndoDialog = (task) => {
    setTaskToUndo(task);
    setUndoDialogOpen(true);
  };

  const handleCloseUndoDialog = () => {
    setUndoDialogOpen(false);
    setTaskToUndo(null);
  };

  const handleConfirmUndo = async () => {
    if (!taskToUndo) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/tasks/${taskToUndo.taskId}/assignment/uncomplete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Task marked as incomplete!");
        fetchMyTasks(); // Refresh the task list
        handleCloseUndoDialog();
      }
    } catch (error) {
      console.error("Error uncompleting task:", error);
      toast.error(error.response?.data?.error || "Failed to uncomplete task");
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#F44336";
      case "medium":
        return "#FF9800";
      case "low":
        return "#4CAF50";
      default:
        return "#757575";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "#4CAF50";
      case "InProgress":
        return "#2196F3";
      case "Assigned":
        return "#FF9800";
      default:
        return "#757575";
    }
  };

  // Calculate dynamic priority based on days remaining until due date
  const calculateDynamicPriority = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 7) {
      return "high";
    } else if (diffDays >= 7 && diffDays <= 15) {
      return "medium";
    } else {
      return "low";
    }
  };

  // Get dynamic priority color based on calculated priority
  const getDynamicPriorityColor = (dueDate) => {
    const dynamicPriority = calculateDynamicPriority(dueDate);
    return getPriorityColor(dynamicPriority);
  };

  // Get dynamic priority label based on calculated priority
  const getDynamicPriorityLabel = (dueDate) => {
    const dynamicPriority = calculateDynamicPriority(dueDate);
    return dynamicPriority.toUpperCase();
  };

  // Calculate days remaining until due date
  const getDaysRemaining = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `${Math.abs(diffDays)} days overdue`;
    } else if (diffDays === 0) {
      return "Due today";
    } else if (diffDays === 1) {
      return "Due tomorrow";
    } else {
      return `${diffDays} days left`;
    }
  };

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "#8B7355",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            My Tasks
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ maxWidth: 1200, mx: "auto", mb: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            placeholder="Search tasks by name, customer name, product code, or bill number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#C4A574" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#2d2d2d",
                color: "#fff",
                "& fieldset": {
                  borderColor: "#444",
                  borderWidth: 1,
                },
                "&:hover fieldset": {
                  borderColor: "#C4A574",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#C4A574",
                },
                "& input::placeholder": {
                  color: "#9CA3AF",
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#C4A574" }} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && filteredTasks.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              bgcolor: "#2d2d2d",
              border: "1px solid #444",
              borderRadius: 2,
              boxShadow: 3,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            <AssignmentIcon sx={{ fontSize: 64, color: "#C4A574", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#C4A574", mb: 1 }}>
              No tasks found
            </Typography>
            <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
              {searchQuery
                ? "Try adjusting your search terms"
                : "You don't have any assigned tasks yet"}
            </Typography>
          </Box>
        )}

        {/* Tasks Grid */}
        {!loading && filteredTasks.length > 0 && (
          <Grid 
            container 
            spacing={{ xs: 1, sm: 2 }} 
            sx={{ 
              maxWidth: 1200, 
              mx: "auto",
              justifyContent: { xs: "center", sm: "flex-start" }
            }}
          >
            {filteredTasks.map((task) => {
              const isCompleted = task.assignmentStatus === "Completed";
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={task.id} sx={{ display: "flex", justifyContent: "center" }}>
                  <Card
                    sx={{
                      height: 420, // Fixed height for uniform cards
                      width: 280, // Fixed width for uniform cards
                      maxWidth: "100%", // Ensure responsiveness on small screens
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      boxShadow: 3,
                      transition: "all 0.2s",
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                      },
                      bgcolor: "#2d2d2d",
                      border: "1px solid #444",
                      borderTop: `4px solid ${isCompleted ? "#9E9E9E" : getDynamicPriorityColor(task.dueDate)}`,
                      opacity: isCompleted ? 0.8 : 1,
                    }}
                  >
                  <CardContent sx={{ flexGrow: 1, pb: 1, bgcolor: "#2d2d2d", display: "flex", flexDirection: "column", height: "calc(100% - 60px)" }}>
                    {/* Priority and Status Chips */}
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}>
                      {!isCompleted && (
                        <Chip
                          label={getDynamicPriorityLabel(task.dueDate)}
                          size="small"
                          sx={{
                            bgcolor: getDynamicPriorityColor(task.dueDate),
                            color: "white",
                            fontWeight: 600,
                            fontSize: { xs: "0.6rem", sm: "0.65rem" },
                            height: { xs: 18, sm: 20 },
                          }}
                        />
                      )}
                      <Chip
                        label={isCompleted ? "My Part: Done" : task.status}
                        size="small"
                        icon={isCompleted ? <CheckCircleIcon sx={{ fontSize: "0.9rem !important" }} /> : undefined}
                        sx={{
                          bgcolor: isCompleted ? "#4CAF50" : getStatusColor(task.status),
                          color: "white",
                          fontWeight: 600,
                          fontSize: { xs: "0.6rem", sm: "0.65rem" },
                          height: { xs: 18, sm: 20 },
                        }}
                      />
                    </Stack>

                    {/* Task Name */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: isCompleted ? "#9E9E9E" : "#C4A574",
                        mb: 1.5,
                        fontSize: { xs: "0.85rem", sm: "0.95rem" },
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        height: { xs: "3.6em", sm: "4.2em" }, // Fixed height for task name
                        textDecoration: isCompleted ? "line-through" : "none",
                      }}
                    >
                      {task.name}
                    </Typography>

                    {/* Task Details */}
                    <Stack spacing={1} sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CalendarTodayIcon
                          sx={{ fontSize: { xs: 14, sm: 16 }, color: isCompleted ? "#9E9E9E" : "#C4A574" }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: { xs: "0.65rem", sm: "0.75rem" },
                              color: isCompleted ? "#9E9E9E" : "#9CA3AF",
                              lineHeight: 1.2,
                            }}
                          >
                            Due: <strong>{task.date}</strong>
                          </Typography>
                          {!isCompleted && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: { xs: "0.6rem", sm: "0.65rem" },
                                color: getDynamicPriorityColor(task.dueDate),
                                fontWeight: 600,
                                lineHeight: 1,
                              }}
                            >
                              {getDaysRemaining(task.dueDate)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: isCompleted ? "#9E9E9E" : "#C4A574" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: isCompleted ? "#9E9E9E" : "#9CA3AF"
                          }}
                        >
                          {task.customerName}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AssignmentIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: isCompleted ? "#9E9E9E" : "#C4A574" }} />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: isCompleted ? "#9E9E9E" : "#9CA3AF",
                            fontWeight: 600
                          }}
                        >
                          Code: {task.productCode}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <ReceiptIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: isCompleted ? "#9E9E9E" : "#C4A574" }} />
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: isCompleted ? "#9E9E9E" : "#9CA3AF"
                          }}
                        >
                          Bill #{task.billNumber}
                        </Typography>
                      </Box>

                      {/* Payment Amount - Fixed position at bottom */}
                      <Box sx={{ mt: "auto" }}>
                        {task.paymentAmount > 0 && (
                          <Box 
                            sx={{ 
                              p: { xs: 0.75, sm: 1 },
                              bgcolor: isCompleted ? "#3a3a3a" : "#1B5E20",
                              borderRadius: 1,
                              border: `1px solid ${isCompleted ? "#555" : "#4CAF50"}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isCompleted ? "#9E9E9E" : "#A5D6A7",
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                display: "block",
                                mb: 0.25,
                              }}
                            >
                              Payment for this task:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isCompleted ? "#9E9E9E" : "#E8F5E9",
                                fontWeight: 700,
                                fontSize: { xs: "0.85rem", sm: "1rem" },
                              }}
                            >
                              ₹{task.paymentAmount.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: "#2d2d2d", display: "flex", gap: 0.5, height: 60 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={() => handleViewTask(task)}
                      sx={{
                        bgcolor: isCompleted ? "#555" : "#C4A574",
                        color: "white",
                        fontWeight: 600,
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        py: { xs: 0.4, sm: 0.5 },
                        "&:hover": {
                          bgcolor: isCompleted ? "#666" : "#A68B5B",
                        },
                      }}
                    >
                      View
                    </Button>
                    {!isCompleted ? (
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenCompletionDialog(task)}
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          py: { xs: 0.4, sm: 0.5 },
                          "&:hover": { bgcolor: "#1b5e20" },
                        }}
                      >
                        Complete
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenUndoDialog(task)}
                        sx={{
                          borderColor: "#ff9800",
                          color: "#ff9800",
                          fontWeight: 600,
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          py: { xs: 0.4, sm: 0.5 },
                          "&:hover": { 
                            borderColor: "#f57c00",
                            bgcolor: "rgba(255, 152, 0, 0.1)"
                          },
                        }}
                      >
                        Undo
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
            })}
          </Grid>
        )}

        {/* Task Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: 2,
              bgcolor: "#2d2d2d",
              border: "1px solid #444"
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#C4A574",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon />
              <Typography variant="subtitle1" fontWeight={700}>
                Task Details
              </Typography>
            </Box>
            <Button
              onClick={handleCloseDetailDialog}
              sx={{ color: "#fff", minWidth: "auto", p: 0.5 }}
            >
              <CloseIcon />
            </Button>
          </DialogTitle>
          <DialogContent sx={{ mt: 3, bgcolor: "#2d2d2d" }}>
            {selectedTask && (
              <Stack spacing={2.5}>
                {/* Task Name */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, color: "#9CA3AF" }}
                  >
                    Task Name
                  </Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ color: "#C4A574" }}>
                    {selectedTask.name}
                  </Typography>
                </Box>

                {/* Product Information */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "#9CA3AF" }}
                  >
                    Product Information
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AssignmentIcon sx={{ fontSize: 18, color: "#C4A574" }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ color: "#E5E7EB", fontWeight: 600 }}>
                          Code: {selectedTask.productCode}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                          {selectedTask.productName}
                        </Typography>
                      </Box>
                      {selectedTask.productCode && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => fetchProductForView(selectedTask.productCode)}
                          sx={{
                            borderColor: "#C4A574",
                            color: "#C4A574",
                            fontSize: "0.75rem",
                            py: 0.5,
                            px: 1.2,
                            whiteSpace: "nowrap",
                            "&:hover": { bgcolor: "rgba(196,165,116,0.1)", borderColor: "#C4A574" },
                          }}
                        >
                          View Product
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </Box>

                {/* Description */}
                {selectedTask.description && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#9CA3AF" }}
                    >
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#E5E7EB" }}>
                      {selectedTask.description}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ borderColor: "#555" }} />

                {/* Priority and Status */}
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#9CA3AF" }}
                    >
                      Priority
                    </Typography>
                    <Chip
                      label={getDynamicPriorityLabel(selectedTask.dueDate)}
                      size="small"
                      sx={{
                        bgcolor: getDynamicPriorityColor(selectedTask.dueDate),
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#9CA3AF" }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={selectedTask.status}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(selectedTask.status),
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#9CA3AF" }}
                    >
                      Due Date
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ color: "#E5E7EB" }}>
                      {selectedTask.date}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: getDynamicPriorityColor(selectedTask.dueDate),
                        fontWeight: 600,
                        display: "block",
                        mt: 0.5
                      }}
                    >
                      {getDaysRemaining(selectedTask.dueDate)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "#555" }} />

                {/* Customer Information */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "#9CA3AF" }}
                  >
                    Customer Information
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 18, color: "#C4A574" }} />
                      <Typography variant="body1" sx={{ color: "#E5E7EB" }}>
                        {selectedTask.customerName}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: "#555" }} />

                {/* Bill Information */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, color: "#9CA3AF" }}
                  >
                    Bill Number
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReceiptIcon sx={{ fontSize: 18, color: "#C4A574" }} />
                    <Typography variant="body1" fontWeight={600} sx={{ color: "#E5E7EB" }}>
                      #{selectedTask.billNumber}
                    </Typography>
                  </Box>
                </Box>

                {/* Product Size */}
                {selectedTask.productSize && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#9CA3AF" }}
                    >
                      Product Size
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#E5E7EB" }}>
                      {selectedTask.productSize}
                    </Typography>
                  </Box>
                )}

                {/* Payment Amount */}
                {selectedTask.paymentAmount > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#1B5E20",
                      borderRadius: 1,
                      border: "2px solid #4CAF50",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: "#A5D6A7" }}
                    >
                      Payment for This Task
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ color: "#E8F5E9" }}
                    >
                      ₹{selectedTask.paymentAmount.toLocaleString('en-IN')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#A5D6A7", mt: 0.5, display: "block" }}
                    >
                      This amount will be added to your monthly salary when you complete this task
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: "#2d2d2d" }}>
            <Button
              onClick={handleCloseDetailDialog}
              variant="outlined"
              sx={{
                borderColor: "#C4A574",
                color: "#C4A574",
                "&:hover": {
                  borderColor: "#A68B5B",
                  bgcolor: "rgba(196, 165, 116, 0.1)",
                },
              }}
            >
              Close
            </Button>
            {selectedTask && selectedTask.status !== "Completed" && (
              <Button
                onClick={() => {
                  handleCloseDetailDialog();
                  handleOpenCompletionDialog(selectedTask);
                }}
                variant="contained"
                sx={{
                  bgcolor: "#4CAF50",
                  color: "#fff",
                  "&:hover": { bgcolor: "#45a049" },
                }}
              >
                Mark Complete
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Task Completion Dialog */}
        <Dialog
          open={completionDialogOpen}
          onClose={handleCloseCompletionDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#2d2d2d",
              border: "1px solid #444"
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#4CAF50",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircleIcon />
            <Typography variant="subtitle1" fontWeight={700}>
              Complete Task
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, bgcolor: "#2d2d2d" }}>
            {selectedTask && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: "#E5E7EB" }}>
                  Are you sure you want to mark this task as complete?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "#9CA3AF" }}>
                  <strong style={{ color: "#C4A574" }}>Task:</strong> {selectedTask.name}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Completion Notes (Optional)"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Add any notes about the completed task..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#3a3a3a",
                      color: "#E5E7EB",
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#4CAF50",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4CAF50",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#9CA3AF",
                      "&.Mui-focused": {
                        color: "#4CAF50",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#2d2d2d" }}>
            <Button
              onClick={handleCloseCompletionDialog}
              disabled={submitting}
              sx={{
                color: "#9CA3AF",
                "&:hover": { bgcolor: "rgba(156, 163, 175, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompleteTask}
              disabled={submitting}
              variant="contained"
              sx={{
                bgcolor: "#4CAF50",
                color: "#fff",
                "&:hover": { bgcolor: "#45a049" },
              }}
            >
              {submitting ? <CircularProgress size={24} /> : "Mark Complete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Undo Completion Confirmation Dialog */}
        <Dialog
          open={undoDialogOpen}
          onClose={handleCloseUndoDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#2d2d2d",
              border: "1px solid #444"
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#ff9800",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Undo Task Completion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, bgcolor: "#2d2d2d" }}>
            {taskToUndo && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: "#E5E7EB" }}>
                  Are you sure you want to mark this task as incomplete?
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#3a3a3a",
                    borderRadius: 1,
                    border: "1px solid #555",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#C4A574" }}>
                    {taskToUndo.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 0.5 }}>
                    {taskToUndo.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                    Customer: {taskToUndo.customerName}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, color: "#F87171", fontWeight: 500 }}>
                  ⚠️ This will revert your completion status and may affect the overall task status.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#2d2d2d" }}>
            <Button
              onClick={handleCloseUndoDialog}
              disabled={submitting}
              sx={{
                color: "#9CA3AF",
                "&:hover": { bgcolor: "rgba(156, 163, 175, 0.1)" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUndo}
              disabled={submitting}
              variant="contained"
              sx={{
                bgcolor: "#ff9800",
                color: "#fff",
                "&:hover": { bgcolor: "#f57c00" },
              }}
            >
              {submitting ? <CircularProgress size={24} /> : "Yes, Undo"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product View Dialog */}
        <Dialog
          open={productViewOpen}
          onClose={() => { setProductViewOpen(false); setViewProduct(null); }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, bgcolor: "#2d2d2d", border: "1px solid #444" } }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#C4A574",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VisibilityIcon />
              <Typography variant="subtitle1" fontWeight={700}>
                Product Details
              </Typography>
            </Box>
            <Button
              onClick={() => { setProductViewOpen(false); setViewProduct(null); }}
              sx={{ color: "#fff", minWidth: "auto", p: 0.5 }}
            >
              <CloseIcon />
            </Button>
          </DialogTitle>

          <DialogContent sx={{ mt: 2, bgcolor: "#2d2d2d" }}>
            {productViewLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress sx={{ color: "#C4A574" }} />
              </Box>
            ) : viewProduct ? (
              <Stack spacing={2.5}>
                {/* Product Image */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {viewProduct.Photo ? (
                    <Box
                      component="img"
                      src={`${API_BASE_URL}${viewProduct.Photo}`}
                      alt={viewProduct.ProductName}
                      sx={{
                        width: "100%",
                        maxHeight: 260,
                        objectFit: "contain",
                        borderRadius: 2,
                        bgcolor: "#1a1a1a",
                        border: "1px solid #444",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#1a1a1a",
                        borderRadius: 2,
                        border: "1px solid #444",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 64, color: "#555" }} />
                    </Box>
                  )}
                </Box>

                <Divider sx={{ borderColor: "#444" }} />

                {/* Name & Code */}
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#C4A574" }}>
                    {viewProduct.ProductName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF", mt: 0.5 }}>
                    Code: {viewProduct.ProductCode}
                  </Typography>
                </Box>

                {/* Core fields */}
                <Grid container spacing={2}>
                  {[
                    { label: "Category", value: viewProduct.Category },
                    { label: "Color", value: viewProduct.Color || "—" },
                    { label: "Price", value: viewProduct.Price ? `₹${viewProduct.Price}` : "—" },
                    { label: "Supplier", value: viewProduct.PurchaseFrom || "—" },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} key={label}>
                      <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block" }}>
                        {label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: "#E5E7EB" }}>
                        {value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* Available Sizes */}
                {viewProduct.sizes && viewProduct.sizes.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block", mb: 0.8 }}>
                      Available Sizes
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {viewProduct.sizes.map((s) => (
                        <Chip
                          key={s.Size}
                          label={`${s.Size} (${s.Stock ?? 0})`}
                          size="small"
                          sx={{ bgcolor: "rgba(196,165,116,0.15)", color: "#C4A574", border: "1px solid rgba(196,165,116,0.3)" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Description */}
                {viewProduct.Description && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block", mb: 0.5 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#E5E7EB" }}>
                      {viewProduct.Description}
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : null}
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: "#2d2d2d" }}>
            <Button
              onClick={() => { setProductViewOpen(false); setViewProduct(null); }}
              variant="outlined"
              sx={{ borderColor: "#C4A574", color: "#C4A574", "&:hover": { bgcolor: "rgba(196,165,116,0.1)" } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </AppLayout>
  );
}
